import diffInSeconds from 'date-fns/differenceInSeconds';
import { jwtDecode } from 'jwt-decode';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import { assign, fromPromise, setup } from 'xstate';

import { Role, SessionSchema } from '@eluve/api-contract';
import { LogLevel, Logger, NoopLogger } from '@eluve/logger';

import { ISessionProvider } from './types';

type Context = {
  jwt: string;
  defaultRole: Role;
  /**
   * If this machine will be used for managing a tenant scoped session,
   * this is the tenant that we ultimately want to have a token for
   */
  desiredTenantId?: string;
  /**
   * The tenant ID that was signed on the current token
   */
  tokenTenantId?: string;
  /**
   * A function that can be used to reset the token eg by starting a new tenant session
   */
  reset: () => Promise<void>;
  attemptingRefreshAfterMs: number;
  sessionProvider: ISessionProvider;
  logger: Logger;
};

type Input = Pick<Context, 'sessionProvider'> & { logger?: Logger };

type Events =
  | { type: 'CHANGE_TENANT'; desiredTenantId: string }
  | { type: 'SUPPLY_RESET_LOGIC'; reset: () => Promise<void> }
  | { type: 'SUPPLY_LOGGER'; logger: Logger }
  | { type: 'ACQUIRE_TOKEN' }
  | { type: 'SESSION_COMPONENT_UNMOUNTED' }
  | { type: 'HARD_REFRESH_REQUESTED' };

export const sessionMachine = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Events,
  },
  actions: {
    logMessage: (
      { context },
      params: {
        level: LogLevel;
        message: string;
        logMeta?: Record<string, unknown>;
      },
    ) => {
      const { level, message, logMeta = {} } = params;
      context.logger[level](message, logMeta);
    },
  },
  guards: {
    isTokenTenantValid: ({ context }) => {
      if (isNil(context.desiredTenantId)) {
        return true;
      }

      return context.tokenTenantId === context.desiredTenantId;
    },
  },
  actors: {
    resetToken: fromPromise<void, { reset: () => Promise<void> }>(
      async ({ input: { reset } }) => await reset(),
    ),
    getToken: fromPromise<
      {
        jwt: string;
        expiresInSeconds: number;
        defaultRole: Role;
        tenantId: string | undefined;
      },
      { sessionProvider: ISessionProvider }
    >(async ({ input: { sessionProvider } }) => {
      const jwt = (await sessionProvider.getAccessToken()) ?? '';

      const payload = await sessionProvider.getAccessTokenPayloadSecurely();

      const result = SessionSchema.parse(payload);
      const hasuraClaims = result['https://hasura.io/jwt/claims'];
      const defaultRole = hasuraClaims['x-hasura-default-role'];

      const accessTokenExpiry = jwtDecode(jwt)?.exp;
      if (!accessTokenExpiry) {
        throw new Error('Access token expiry not found');
      }

      const now = Math.floor(Date.now() / 1000);
      const expiresInSeconds = diffInSeconds(
        new Date(accessTokenExpiry * 1000),
        new Date(now * 1000),
      );

      return {
        expiresInSeconds,
        jwt,
        defaultRole,
        tenantId: hasuraClaims['x-hasura-tenant-id'],
      };
    }),

    refreshToken: fromPromise<boolean, { sessionProvider: ISessionProvider }>(
      async ({ input: { sessionProvider } }) => {
        return sessionProvider.attemptRefreshingSession();
      },
    ),
  },
  delays: {
    preemptivelyRefreshToken: ({ context }) => {
      return context.attemptingRefreshAfterMs;
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QGU6wJYHsB2ACAsgIYDGAFutmAMQCCAwgIoCqAkgEoCiA+gCoDyAaQ4A5ANoAGALqJQAB0wYALlmwyQAD0QBWAOwAaEAE9EAJgAcAZgB0ATksWAbCfNmtARnE6HAX28HUsBg4BCTklFR0ABI0wgDi3DwiMTwS0kgg8koqapoIugbGCBbFVg72DgAsjjZOFTq+-mgqIWQU1MhMAAqdADIAmlycyBw8XD18sSx0qWqZ6Mo4OYhuFVoVtmZeFiYWZnbiZhUFiDY2blbiJu7ixWXipw0gAUF4RK3hHd39YxPxbDPpOYLVTpXIrHTrCpXNzFHRXG4OY4INwmR7PZpvMLtDjIZAsPjCLh0Pj4ToEkSjJjCfB8KmJAAiALkCnm2VB2n0RmWXlKWnE-NO7hRbh8fieTWCmLaVmEmB4mAA1mBsFQmRkWcClsi9g4rCitCY3DY6lV+YiuUUqlYLDo+dtxDCHJcLGiJa9QtKaMQAI4AV3QACcKFB5UqVRAcGArBQAG6KqPoyUeyhWL1+wPB0PKhCxzDEQjA1JqoFs0Bgs42a38w02G5uLxHC2VdbiPmeCriBxrZyosWJ93vKNp-1B7Ah+MqsABgOYANWWQAGwLADNZwBbKz9lpY1M+keZic57Bx-OFqTFjWljSmCoVXUWPkmCrGnY1CFIhxuLTW1atrt2CxOxdPs3W3aUADVCAXdAIALA8w1VKRZkvRZ2TySoLn5G4nx2EwdEcJFn11Zw+ThLQnQfB96hAwIMWTKNIOg2DlDHLMVVENw0mZLJULLbQMKw7CoW2fDzUKQ5Kx0W0Dg8VtLhMGxXVopNBysNg4DARQWPHBCIxTXMlU3UCpRTdTYE07S2KPE84JwIskMBFCQT47Uaj1K4axNQDOyRHQbGsfVOy-KTxGfMwlJeMDTI0rT4OVKgpxnOdFxXdcjOUgcdzMiy4uway81s7B7K49UeOc69XN1fVPIhbyxMQSxKzMeEHAsVYTAOG0Iro1T1OXAM4DCHT4r0qMDITYz6LUsB+sG3L8tPFRiuQsqtTcHV3INI0vLNJF3ErPDSN2UKu3rbqVKymaBtgIa2IS6dZ3nJdFFXAMNy3Eyoz667bsPXNFrs88HO41leIq9a3Oq7bat2i0O3EC5pOKMwUdapxzsy6U2JoGNCHQJcACMF2odRYEUAso0IZdFCnAAKWQBrANdZGUGMwAXQxvsGtiAEoqA+qbsdx-HCCJsAL1WtC3C-c5PxMT9XA8swu0ImxvzqUj4RR4otGAsVsEwCA4DUAXBxW0HytyGwkT2a0207CFzFWT8MaiqMWAgYnzc1ND5Y63lLnItXDmfLQPyNKx5dImF9ThYDGgyt2ZTlCdvavMF3HWEUyja+XVh0VwkQsCPmsfVwzBhNqtFdz7d3TUdhvKkswdyDrjVsG18M8PCYSqPa7AuX84QcMpXBqGupsYmC4NY1PHMllz8gtAuzFsLCC5WVrTgqCfepiyy55Bn2XJWULNs3xx7g6ixfNcKwyM8fDkdC8KaMi2uuZu3K05b5ZdBMKwbZ1ruBzsaRshRbznBsC1NWWgUY1FCrvHcQs8aEy9vPC2WopKr2gXyXWToV6rEImsWw8IdChX2m4XwvggA */
  id: 'Session Machine',
  context: ({ input: { sessionProvider, logger } }) => ({
    jwt: '',
    defaultRole: 'user',
    attemptingRefreshAfterMs: 0,
    sessionProvider,
    logger:
      logger ?? new Logger('Session Machine', { logger: new NoopLogger() }),
    reset: async () => noop(),
  }),
  on: {
    ACQUIRE_TOKEN: {
      target: '.NoToken',
    },
    CHANGE_TENANT: {
      actions: assign({
        desiredTenantId: ({ event }) => event.desiredTenantId,
        tokenTenantId: undefined,
        jwt: '',
      }),
      target: '.NoToken',
    },
    SUPPLY_RESET_LOGIC: {
      actions: assign({
        reset: ({ event }) => event.reset,
      }),
    },
    SUPPLY_LOGGER: {
      actions: assign({
        logger: ({ event }) => event.logger,
      }),
    },
    SESSION_COMPONENT_UNMOUNTED: {
      actions: assign({
        jwt: '',
        tokenTenantId: undefined,
      }),
      target: '.Idle',
    },
    HARD_REFRESH_REQUESTED: {
      actions: assign({
        jwt: '',
      }),
      target: '.Idle',
    },
  },
  states: {
    Idle: {
      description:
        'Idle state. Waiting to receive a command to start acquiring a token.',
    },
    NoToken: {
      description: 'No token available',
      always: {
        target: 'AcquiringToken',
      },
    },
    AcquiringToken: {
      description: 'Extracting JWT and core values from session data',
      entry: {
        type: 'logMessage',
        params: () => ({
          level: 'info',
          message: 'Acquiring token',
        }),
      },
      invoke: {
        src: 'getToken',
        input: ({ context }) => ({
          sessionProvider: context.sessionProvider,
        }),
        onDone: [
          {
            target: 'ValidatingToken',
            actions: assign({
              defaultRole: ({ event }) => event.output.defaultRole,
              jwt: ({ event }) => event.output.jwt,
              tokenTenantId: ({ event }) => event.output.tenantId,
              attemptingRefreshAfterMs: ({
                event: {
                  output: { expiresInSeconds },
                },
              }) => {
                const val = expiresInSeconds * 0.8 * 1000;
                return val;
              },
            }),
          },
        ],
        onError: {
          actions: [
            {
              type: 'logMessage',
              params: {
                level: 'warn',
                message: 'Failed to acquire token',
              },
            },
            assign({
              jwt: '',
              tokenTenantId: undefined,
            }),
          ],
          target: 'Idle',
        },
      },
    },
    ValidatingToken: {
      description:
        'Validating that the token is usable for the given call site. Eg. tenant id matches',
      always: [
        {
          guard: 'isTokenTenantValid',
          target: 'TokenAvailable',
        },
        {
          target: 'ResettingToken',
        },
      ],
    },
    ResettingToken: {
      description:
        'Attemping to reset the token eg. by starting a new tenant session if the tenant id did not match',
      entry: {
        type: 'logMessage',
        params: () => ({
          level: 'info',
          message: 'Resetting token',
        }),
      },
      invoke: {
        src: 'resetToken',
        input: ({ context }) => ({
          reset: context.reset,
        }),
        onDone: {
          target: 'AcquiringToken',
        },
        onError: {
          actions: [
            {
              type: 'logMessage',
              params: {
                level: 'warn',
                message: 'Failed to reset token',
              },
            },
            assign({
              jwt: '',
              tokenTenantId: undefined,
            }),
          ],
          target: 'Idle',
        },
      },
    },
    RefreshingToken: {
      description: 'Attempting to refresh the token',
      entry: {
        type: 'logMessage',
        params: () => ({
          level: 'info',
          message: 'Attempting to refresh token',
        }),
      },
      invoke: {
        src: 'refreshToken',
        input: ({ context: { sessionProvider } }) => ({ sessionProvider }),
        onDone: {
          target: 'AcquiringToken',
        },
        onError: {
          actions: [
            {
              type: 'logMessage',
              params: {
                level: 'warn',
                message: 'Failed to refresh token',
              },
            },
            assign({
              jwt: '',
              tokenTenantId: undefined,
            }),
          ],
          target: 'Idle',
        },
      },
    },
    TokenAvailable: {
      description: 'A valid token is available to be used to make requests',
      entry: {
        type: 'logMessage',
        params: ({ context }) => ({
          level: 'info',
          message: `Token acquired. Refreshing in ${context.attemptingRefreshAfterMs}ms`,
        }),
      },
      after: {
        preemptivelyRefreshToken: 'RefreshingToken',
      },
    },
  },
  initial: 'Idle',
});
